import React, { useState } from 'react';
import { ICON_CORNER_RADIUS } from '../lib/imageToIconPng';

interface AppIconImageProps {
  src: string;
  alt: string;
  /** Sizes (and may pad) the square box the image fills. */
  className?: string;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  onError?: () => void;
}

// The letter tile's corners: ICON_CORNER_RADIUS of the side.
const cornerRadius = `${ICON_CORNER_RADIUS * 100}%`;

/**
 * An image as the Domain Expert app shows its icon: a full square like the letter tile, filling
 * its box edge to edge. Any other shape is cropped to its centre square, and the corners are
 * rounded by ICON_CORNER_RADIUS of the side.
 */
export default function AppIconImage({ src, alt, className = '', onLoad, onError }: Readonly<AppIconImageProps>) {
  // A src that failed to load shows as a plain <img> does: the browser's broken image and alt text.
  // Otherwise the corners are rounded from the start, so they never show square while it loads.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = failedSrc === src;

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setFailedSrc(null);
    onLoad?.(event);
  };

  const handleError = () => {
    setFailedSrc(src);
    onError?.();
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <img
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={`h-full w-full ${failed ? 'object-contain' : 'object-cover'}`}
        style={failed ? undefined : { borderRadius: cornerRadius }}
      />
    </div>
  );
}
